import React, { useEffect, useRef, useState } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { navMenu } from '../data';
import SignInBox from '../components/SignInBox';
import SignInRequiredModal from '../components/SignInRequiredModal';
import './navHelpers/Navbar.css';

const publicSeoNavItems = [
  {
    menu: 'Generator',
    link: '/english-to-spanish-subtitle-generator',
  },
  {
    menu: 'Blog',
    link: '/blog',
  },
];

const Navbar = ({
  isUserSignedIn,
  fetchingLoggedIn,
  handleLogout,
  user,
  setIsUserSignedIn,
  setUser,
  doctors,
  setDoctors,
  insurances,
  setInsurances,
  bodily_parts,
  setBodilyParts,
  originalDoctors,
  setOriginalDoctors,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const accountMenuRef = useRef(null);
  const authModalDismissedRef = useRef(false);
  const location = useLocation();

  const signedIn = Boolean(user && isUserSignedIn);
  const isCheckingAuth = Boolean(fetchingLoggedIn);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((previousValue) => !previousValue);
  };

  const openAuthModal = () => {
    if (isCheckingAuth) return;

    authModalDismissedRef.current = false;
    setShowAuthModal(true);
    setIsAccountMenuOpen(false);
    setIsMobileMenuOpen(false);
  };

  const closeAuthModal = () => {
    authModalDismissedRef.current = true;
    setShowAuthModal(false);
  };

  const toggleAccountMenu = () => {
    if (isCheckingAuth) return;

    if (!signedIn) {
      openAuthModal();
      return;
    }

    setIsAccountMenuOpen((previousValue) => !previousValue);
  };

  const closeAccountMenu = () => {
    setIsAccountMenuOpen(false);
  };

  const resetAllMenus = () => {
    setIsMobileMenuOpen(false);
    setIsAccountMenuOpen(false);
  };

  const handleProtectedNavClick = (event) => {
    if (isCheckingAuth) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (!signedIn) {
      event.preventDefault();
      event.stopPropagation();
      openAuthModal();
      return;
    }

    resetAllMenus();
  };

  const handlePublicHomeClick = () => {
    resetAllMenus();
  };

  const handlePublicSeoNavClick = () => {
    resetAllMenus();
  };

  const getDisplayName = () => {
    return user?.username || user?.email || 'Account';
  };

  const getUserInitial = () => {
    const displayName = getDisplayName();

    return displayName.charAt(0).toUpperCase();
  };

  const getRoleLabel = () => {
    if (user?.super_admin) return 'Super Admin';
    if (user?.admin) return 'Admin';

    return 'User';
  };

  const visibleNavItems = navMenu.filter((item) => item.menu !== 'Sign in with X');

  useEffect(() => {
    if (signedIn) {
      authModalDismissedRef.current = false;
      setShowAuthModal(false);
      setIsAccountMenuOpen(false);
      setIsMobileMenuOpen(false);
    }
  }, [signedIn]);

  useEffect(() => {
    const handleDocumentMouseDown = (event) => {
      if (
        accountMenuRef.current &&
        !accountMenuRef.current.contains(event.target)
      ) {
        closeAccountMenu();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        closeAccountMenu();
        closeAuthModal();
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown);
    document.addEventListener('keydown', handleEscapeKey);

    return () => {
      document.removeEventListener('mousedown', handleDocumentMouseDown);
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, []);

  const renderAuthSpinner = ({ mobile = false } = {}) => {
    return (
      <span
        className={[
          'flex shrink-0 items-center justify-center rounded-xl border border-cyan-300/25 bg-cyan-400/15 shadow-lg shadow-cyan-950/20',
          mobile ? 'h-10 w-10' : 'h-9 w-9',
        ].join(' ')}
      >
        <span
          className={[
            'animate-spin rounded-full border-2 border-cyan-100/25 border-t-cyan-100',
            mobile ? 'h-5 w-5' : 'h-4 w-4',
          ].join(' ')}
        />
      </span>
    );
  };

  const renderAccountButton = ({ mobile = false } = {}) => {
    return (
      <button
        type="button"
        onClick={toggleAccountMenu}
        aria-haspopup="menu"
        aria-expanded={signedIn ? isAccountMenuOpen : showAuthModal}
        aria-busy={isCheckingAuth}
        disabled={isCheckingAuth}
        className={[
          'group relative inline-flex items-center justify-center overflow-hidden rounded-2xl border text-left shadow-lg shadow-cyan-950/25 transition',
          mobile ? 'min-h-[46px] w-full px-4 py-3' : 'min-h-[48px] px-3.5 py-2',
          isCheckingAuth
            ? 'cursor-wait border-cyan-300/20 bg-slate-950/70 text-slate-200'
            : isAccountMenuOpen || showAuthModal
              ? 'border-cyan-300/40 bg-cyan-400/15 text-cyan-100'
              : 'border-cyan-300/15 bg-slate-950/60 text-slate-200 hover:border-cyan-300/35 hover:bg-cyan-400/10 hover:text-cyan-100',
        ].join(' ')}
      >
        <span className="absolute inset-0 translate-x-[-120%] bg-gradient-to-r from-transparent via-cyan-200/10 to-transparent transition duration-700 group-hover:translate-x-[120%]" />

        <span className="relative flex min-w-0 items-center gap-3">
          {isCheckingAuth ? (
            renderAuthSpinner({ mobile })
          ) : (
            <span
              className={[
                'flex shrink-0 items-center justify-center rounded-xl border border-cyan-300/25 bg-cyan-400/15 font-black text-cyan-100 shadow-lg shadow-cyan-950/20',
                mobile ? 'h-10 w-10 text-base' : 'h-9 w-9 text-sm',
              ].join(' ')}
            >
              {signedIn ? getUserInitial() : '🔐'}
            </span>
          )}

          <span className="min-w-0">
            <span className="block text-[10px] font-black uppercase tracking-[0.22em] text-cyan-300">
              {isCheckingAuth ? 'Checking Access' : signedIn ? 'Account' : 'Beta Access'}
            </span>

            <span
              className={[
                'mt-0.5 block truncate font-black text-white',
                mobile ? 'text-sm' : 'max-w-[135px] text-xs',
              ].join(' ')}
            >
              {isCheckingAuth ? 'Loading...' : signedIn ? getDisplayName() : 'Sign In / Sign Up'}
            </span>

            {isCheckingAuth && mobile && (
              <span className="mt-0.5 block text-xs font-semibold text-slate-400">
                Restoring your session
              </span>
            )}

            {signedIn && !isCheckingAuth && mobile && (
              <span className="mt-0.5 block text-xs font-semibold text-slate-400">
                {getRoleLabel()}
              </span>
            )}

            {!signedIn && !isCheckingAuth && mobile && (
              <span className="mt-0.5 block text-xs font-semibold text-slate-400">
                Create a beta account
              </span>
            )}
          </span>
        </span>

        <span
          className={[
            'relative ml-3 flex shrink-0 items-center justify-center rounded-xl border border-cyan-300/15 bg-slate-950/60 text-cyan-200 transition',
            mobile ? 'h-8 w-8' : 'h-7 w-7',
            isAccountMenuOpen ? 'rotate-180' : '',
          ].join(' ')}
        >
          {isCheckingAuth ? (
            <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-cyan-100/25 border-t-cyan-100" />
          ) : signedIn ? (
            '˅'
          ) : (
            '›'
          )}
        </span>
      </button>
    );
  };

  const renderAccountDropdown = ({ mobile = false } = {}) => {
    if (!signedIn || !isAccountMenuOpen || isCheckingAuth) return null;

    return (
      <div
        className={[
          'z-50 overflow-hidden rounded-3xl border border-cyan-300/20 bg-slate-950/95 shadow-2xl shadow-cyan-950/40 backdrop-blur-2xl',
          mobile
            ? 'mt-3 w-full'
            : 'absolute right-0 top-[calc(100%+0.75rem)] w-[min(92vw,390px)]',
        ].join(' ')}
        role="menu"
      >
        <SignInBox
          variant="navbar"
          isUserSignedIn={isUserSignedIn}
          handleLogout={() => {
            closeAccountMenu();

            if (handleLogout) {
              handleLogout();
            }
          }}
          user={user}
          setIsUserSignedIn={setIsUserSignedIn}
          setUser={setUser}
          doctors={doctors}
          setDoctors={setDoctors}
          insurances={insurances}
          setInsurances={setInsurances}
          bodily_parts={bodily_parts}
          setBodilyParts={setBodilyParts}
          originalDoctors={originalDoctors}
          setOriginalDoctors={setOriginalDoctors}
          onSuccessfulSignIn={closeAccountMenu}
        />
      </div>
    );
  };

  return (
    <>
      <header className="sticky top-0 z-40 w-full border-b border-cyan-300/10 bg-slate-950/80 text-white shadow-2xl shadow-slate-950/40 backdrop-blur-2xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_10%,rgba(34,211,238,0.16),transparent_30%),linear-gradient(90deg,rgba(15,23,42,0.98),rgba(30,41,59,0.84),rgba(15,23,42,0.98))]" />

        <div className="relative mx-auto flex w-full max-w-7xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <Link
            to="/"
            onClick={handlePublicHomeClick}
            className="group flex min-w-0 items-center gap-3"
          >
            <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-2xl border border-cyan-300/20 bg-cyan-400/10 shadow-lg shadow-cyan-950/30 sm:h-16 sm:w-16">
              <img
                src="/BlueLogo.png"
                alt="Subtitle Tsunami"
                className="h-full w-full object-contain p-1.5 transition duration-300 group-hover:scale-105"
              />
            </div>

            <div className="min-w-0">
              <p className="truncate text-xs font-black uppercase tracking-[0.28em] text-cyan-300">
                Subtitle
              </p>

              <p className="truncate text-lg font-black leading-tight tracking-tight text-white sm:text-xl">
                Tsunami
              </p>
            </div>
          </Link>

          <div className="flex items-center gap-2 lg:hidden">
            <div ref={accountMenuRef} className="relative">
              <button
                type="button"
                onClick={toggleAccountMenu}
                aria-label={
                  isCheckingAuth
                    ? 'Checking sign in status'
                    : signedIn
                      ? 'Open account menu'
                      : 'Open sign in and sign up modal'
                }
                aria-haspopup="menu"
                aria-expanded={signedIn ? isAccountMenuOpen : showAuthModal}
                aria-busy={isCheckingAuth}
                disabled={isCheckingAuth}
                className={[
                  'inline-flex h-11 w-11 items-center justify-center rounded-2xl border text-sm font-black shadow-lg shadow-cyan-950/20 transition',
                  isCheckingAuth
                    ? 'cursor-wait border-cyan-300/20 bg-slate-950/70 text-cyan-100'
                    : isAccountMenuOpen || showAuthModal
                      ? 'border-cyan-300/40 bg-cyan-400/15 text-cyan-100'
                      : 'border-cyan-300/20 bg-cyan-400/10 text-cyan-100 hover:border-cyan-200/50 hover:bg-cyan-300/15',
                ].join(' ')}
              >
                {isCheckingAuth ? (
                  <span className="h-5 w-5 animate-spin rounded-full border-2 border-cyan-100/25 border-t-cyan-100" />
                ) : signedIn ? (
                  getUserInitial()
                ) : (
                  '🔐'
                )}
              </button>
            </div>

            <button
              type="button"
              onClick={toggleMobileMenu}
              className="inline-flex h-11 w-11 items-center justify-center rounded-2xl border border-cyan-300/20 bg-cyan-400/10 text-cyan-100 shadow-lg shadow-cyan-950/20 transition hover:border-cyan-200/50 hover:bg-cyan-300/15"
              aria-label={isMobileMenuOpen ? 'Close navigation menu' : 'Open navigation menu'}
              aria-expanded={isMobileMenuOpen}
            >
              <span className="text-xl">
                {isMobileMenuOpen ? '×' : '☰'}
              </span>
            </button>
          </div>

          <div className="hidden items-center gap-3 lg:flex">
            <nav className="flex items-center gap-2">
              {visibleNavItems.map((item) => {
                const isHomeLink = item.link === '/';
                const isSchedulerSection =
                  item.menu === 'Scheduler App' &&
                  location.pathname !== '/' &&
                  (location.pathname === '/schedulerApp' || location.pathname === '/edit');

                return (
                  <div key={item.menu} className="relative">
                    <NavLink
                      onClick={isHomeLink ? handlePublicHomeClick : handleProtectedNavClick}
                      to={item.link}
                      className={({ isActive }) =>
                        [
                          'group relative inline-flex min-h-[44px] items-center justify-center rounded-2xl px-4 py-2 text-sm font-black uppercase tracking-[0.12em] transition',
                          isActive
                            ? 'border border-cyan-300/25 bg-cyan-400/15 text-cyan-100 shadow-lg shadow-cyan-950/20'
                            : 'border border-transparent text-slate-300 hover:border-cyan-300/20 hover:bg-cyan-400/10 hover:text-cyan-100',
                        ].join(' ')
                      }
                    >
                      {item.menu}
                    </NavLink>

                    {isSchedulerSection && (
                      <div className="absolute left-1/2 top-[calc(100%+0.75rem)] z-50 flex -translate-x-1/2 overflow-hidden rounded-2xl border border-cyan-300/15 bg-slate-950/95 p-1 shadow-2xl shadow-slate-950/50 backdrop-blur-xl">
                        <NavLink
                          to="/schedulerApp"
                          onClick={handleProtectedNavClick}
                          className={({ isActive }) =>
                            [
                              'inline-flex min-h-[38px] items-center justify-center rounded-xl px-4 text-xs font-black uppercase tracking-[0.12em] transition',
                              isActive
                                ? 'bg-cyan-400/15 text-cyan-100'
                                : 'text-slate-400 hover:bg-cyan-400/10 hover:text-cyan-100',
                            ].join(' ')
                          }
                        >
                          Search
                        </NavLink>

                        <NavLink
                          to="/edit"
                          onClick={handleProtectedNavClick}
                          className={({ isActive }) =>
                            [
                              'inline-flex min-h-[38px] items-center justify-center rounded-xl px-4 text-xs font-black uppercase tracking-[0.12em] transition',
                              isActive
                                ? 'bg-cyan-400/15 text-cyan-100'
                                : 'text-slate-400 hover:bg-cyan-400/10 hover:text-cyan-100',
                            ].join(' ')
                          }
                        >
                          Admin
                        </NavLink>
                      </div>
                    )}
                  </div>
                );
              })}

              {publicSeoNavItems.map((item) => {
                return (
                  <NavLink
                    key={item.menu}
                    to={item.link}
                    onClick={handlePublicSeoNavClick}
                    className={({ isActive }) =>
                      [
                        'group relative inline-flex min-h-[44px] items-center justify-center rounded-2xl px-4 py-2 text-sm font-black uppercase tracking-[0.12em] transition',
                        isActive
                          ? 'border border-cyan-300/25 bg-cyan-400/15 text-cyan-100 shadow-lg shadow-cyan-950/20'
                          : 'border border-transparent text-slate-300 hover:border-cyan-300/20 hover:bg-cyan-400/10 hover:text-cyan-100',
                      ].join(' ')
                    }
                  >
                    {item.menu}
                  </NavLink>
                );
              })}
            </nav>

            <div ref={accountMenuRef} className="relative">
              {renderAccountButton()}
              {renderAccountDropdown()}
            </div>
          </div>
        </div>

        {signedIn && isAccountMenuOpen && !isCheckingAuth && (
          <div className="relative border-t border-cyan-300/10 bg-slate-950/95 px-4 pb-4 pt-3 shadow-2xl shadow-slate-950/50 backdrop-blur-2xl lg:hidden">
            <div className="mx-auto w-full max-w-7xl">
              {renderAccountButton({ mobile: true })}
              {renderAccountDropdown({ mobile: true })}
            </div>
          </div>
        )}

        {isMobileMenuOpen && (
          <div className="relative border-t border-cyan-300/10 bg-slate-950/95 px-4 pb-4 pt-2 shadow-2xl shadow-slate-950/50 backdrop-blur-2xl lg:hidden">
            <nav className="mx-auto grid w-full max-w-7xl gap-2">
              {isCheckingAuth && (
                <div className="mb-2 flex min-h-[58px] items-center justify-between rounded-2xl border border-cyan-300/15 bg-slate-950/70 px-4 text-left text-sm font-black uppercase tracking-[0.14em] text-cyan-100 shadow-lg shadow-cyan-950/20">
                  <span className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-300/25 bg-cyan-400/15">
                      <span className="h-5 w-5 animate-spin rounded-full border-2 border-cyan-100/25 border-t-cyan-100" />
                    </span>

                    <span>
                      <span className="block text-[10px] text-cyan-300">
                        Checking Access
                      </span>

                      <span className="block">
                        Loading Account
                      </span>
                    </span>
                  </span>
                </div>
              )}

              {!signedIn && !isCheckingAuth && (
                <button
                  type="button"
                  onClick={openAuthModal}
                  className="mb-2 flex min-h-[58px] items-center justify-between rounded-2xl border border-cyan-300/20 bg-cyan-400/10 px-4 text-left text-sm font-black uppercase tracking-[0.14em] text-cyan-100 shadow-lg shadow-cyan-950/20 transition hover:border-cyan-200/50 hover:bg-cyan-300/15"
                >
                  <span className="flex items-center gap-3">
                    <span className="flex h-10 w-10 items-center justify-center rounded-xl border border-cyan-300/25 bg-cyan-400/15">
                      🔐
                    </span>

                    <span>
                      <span className="block text-[10px] text-cyan-300">
                        Beta Access
                      </span>

                      <span className="block">
                        Sign In / Sign Up
                      </span>
                    </span>
                  </span>

                  <span className="text-cyan-300/70">›</span>
                </button>
              )}

              {visibleNavItems.map((item) => {
                const isHomeLink = item.link === '/';
                const isSchedulerApp = item.menu === 'Scheduler App';
                const showSchedulerSubnav =
                  isSchedulerApp &&
                  location.pathname !== '/' &&
                  (location.pathname === '/schedulerApp' || location.pathname === '/edit');

                return (
                  <div key={item.menu} className="rounded-2xl border border-white/5 bg-white/[0.03] p-1">
                    <NavLink
                      onClick={isHomeLink ? handlePublicHomeClick : handleProtectedNavClick}
                      to={item.link}
                      className={({ isActive }) =>
                        [
                          'flex min-h-[46px] items-center justify-between rounded-xl px-4 text-sm font-black uppercase tracking-[0.14em] transition',
                          isActive
                            ? 'bg-cyan-400/15 text-cyan-100'
                            : 'text-slate-300 hover:bg-cyan-400/10 hover:text-cyan-100',
                        ].join(' ')
                      }
                    >
                      <span>{item.menu}</span>
                      <span className="text-cyan-300/70">›</span>
                    </NavLink>

                    {showSchedulerSubnav && (
                      <div className="mt-1 grid grid-cols-2 gap-1">
                        <NavLink
                          to="/schedulerApp"
                          onClick={handleProtectedNavClick}
                          className={({ isActive }) =>
                            [
                              'flex min-h-[40px] items-center justify-center rounded-xl text-xs font-black uppercase tracking-[0.12em] transition',
                              isActive
                                ? 'bg-cyan-400/15 text-cyan-100'
                                : 'text-slate-400 hover:bg-cyan-400/10 hover:text-cyan-100',
                            ].join(' ')
                          }
                        >
                          Search
                        </NavLink>

                        <NavLink
                          to="/edit"
                          onClick={handleProtectedNavClick}
                          className={({ isActive }) =>
                            [
                              'flex min-h-[40px] items-center justify-center rounded-xl text-xs font-black uppercase tracking-[0.12em] transition',
                              isActive
                                ? 'bg-cyan-400/15 text-cyan-100'
                                : 'text-slate-400 hover:bg-cyan-400/10 hover:text-cyan-100',
                            ].join(' ')
                          }
                        >
                          Admin
                        </NavLink>
                      </div>
                    )}
                  </div>
                );
              })}

              {publicSeoNavItems.map((item) => {
                return (
                  <div key={item.menu} className="rounded-2xl border border-white/5 bg-white/[0.03] p-1">
                    <NavLink
                      to={item.link}
                      onClick={handlePublicSeoNavClick}
                      className={({ isActive }) =>
                        [
                          'flex min-h-[46px] items-center justify-between rounded-xl px-4 text-sm font-black uppercase tracking-[0.14em] transition',
                          isActive
                            ? 'bg-cyan-400/15 text-cyan-100'
                            : 'text-slate-300 hover:bg-cyan-400/10 hover:text-cyan-100',
                        ].join(' ')
                      }
                    >
                      <span>{item.menu}</span>
                      <span className="text-cyan-300/70">›</span>
                    </NavLink>
                  </div>
                );
              })}
            </nav>
          </div>
        )}
      </header>

      <SignInRequiredModal
        isOpen={showAuthModal}
        onClose={closeAuthModal}
        isUserSignedIn={isUserSignedIn}
        handleLogout={handleLogout}
        user={user}
        setIsUserSignedIn={setIsUserSignedIn}
        setUser={setUser}
        setDoctors={setDoctors}
        setInsurances={setInsurances}
        setBodilyParts={setBodilyParts}
        setOriginalDoctors={setOriginalDoctors}
      />
    </>
  );
};

export default Navbar;